import * as React from "react";
import { Button, Card } from "antd";
import { CardTitle } from "../../components/antd/card/CardTitle";
import { CloseOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { CardContent } from "../../components/antd/card/CardContent";
import { useTableItemQueryData } from "../../newcomponents/table/data/useTableItemQueryData";
import { useTableKindId } from "../../newcomponents/table/data/useTableKindId";
import { parseLink } from "../../utils/linkParser/linkParser";
import { TableHeaderRefreshButton } from "../../newcomponents/table/components/RefreshButton";
import { CustomTableCPLNQuery } from "../../newcomponents/table/components/TableCplnQuery";
import { TagsColumn } from "../../newcomponents/table/columns/wellKnown/tagsColumn";
import { TableModal } from "../../components/antd/TableModal";
import { Table } from "../../newcomponents/table/table";
import { Location } from "../../schema/types/location/entity";
import { NameDescriptionColumn } from "../../newcomponents/table/columns/wellKnown/nameDescriptionColumn";
import { ProviderColumn } from "../../newcomponents/table/columns/wellKnown/providerColumn";
import { NameDescriptionNoLinkColumn } from "../../newcomponents/table/columns/wellKnown/nameDescriptionNoLinkColumn";
import { GvcInput } from "../../schema/types/gvc/input";
import { getLocationTableItem, LocationTableItem } from "../../mst/kinds/location.helpers";
import { useCpFormContext } from "../../components/form/useCpFormContext";
import { formSetValue, formTriggerValidation, useCpFormValue } from "./testNewForm/utils";
import { store } from "../../store";
import { cloneDeep } from "lodash";
import { KindSelectState } from "../../components/antd/kindselect/KindSelect";

interface Props {}
export const LocationItems: React.FC<Props> = ({}) => {
  const { id } = useCpFormContext();

  const { kind: addTableKind, id: addTableId } = useTableKindId("location", "gvc-location-items");
  const qData = useTableItemQueryData<Location>(addTableKind, {});

  const [selectionsToAdd, setSelectionsToAdd] = React.useState<string[]>([]);
  const [selectionItems, setSelectionItems] = React.useState<LocationTableItem[]>([]);
  const [itemCache, setItemCache] = React.useState<Record<string, LocationTableItem>>({});
  const [isAddingItem, setIsAddingItem] = React.useState<boolean>(false);

  const locations = useCpFormValue(`locationItems`);
  const locationTableItems: LocationTableItem[] = React.useMemo(() => locations.map((l) => getLocationTableItem(l)), [
    locations,
  ]);

  const onRemoveItem = async (index: number) => {
    const form = store.getState().form.instances[id];
    const values: GvcInput = form.values as any;

    const currentLocations: KindSelectState[] = cloneDeep(values.locationItems);
    currentLocations.splice(index, 1);
    formSetValue(id, `locationItems`, currentLocations);

    formTriggerValidation(id);
  };

  function onOkAddModal() {
    const form = store.getState().form.instances[id];
    const values: GvcInput = form.values as any;

    const currentLocations: KindSelectState[] = cloneDeep(values.locationItems);

    // TODO selectionsToAdd can be renamed to something with link
    for (const selectionToAdd of selectionsToAdd) {
      const currentLinks = currentLocations.map((u) => u.link);
      const item = selectionItems.find((i) => i.selfLink === selectionToAdd);
      if (item) {
        itemCache[selectionToAdd] = item;
      }

      if (currentLinks.includes(selectionToAdd)) {
        continue;
      }

      const { name } = parseLink(selectionToAdd, { useInputCtx: true });
      currentLocations.push({
        value: name,
        link: selectionToAdd,
        item: item,
      });
    }
    setItemCache(itemCache);

    formSetValue(id, `locationItems`, currentLocations);

    setSelectionsToAdd([]);
    setSelectionItems([]);
    setIsAddingItem(false);

    formTriggerValidation(id);
  }

  const currentLinks = locations.map((l) => l.link);
  const addLocationTableItems: LocationTableItem[] = qData.visibleItems
    .filter((item) => item.spec?.enabled === true)
    .map((item) => getLocationTableItem({ item: item }))
    .filter((item) => !currentLinks.includes(item.selfLink));

  return (
    <>
      <div className="cpln-form-section cpln-ant-card-container">
        <Card>
          <CardTitle>Locations</CardTitle>
          <CardContent>
            {locationTableItems.length > 0 ? (
              <Table<LocationTableItem>
                tableId="gvc-location-items-editable"
                data={locationTableItems}
                hideSettings
                columns={[
                  NameDescriptionNoLinkColumn({ enableSort: false }),
                  ProviderColumn(),
                  TagsColumn(),
                  {
                    id: "action",
                    label: "",
                    enableResize: false,
                    size: 120,
                    cell: ({ row }) => {
                      return (
                        <div className="w-full flex justify-end">
                          <Button
                            htmlType={`button`}
                            type={"text"}
                            icon={<CloseOutlined />}
                            danger
                            onClick={() => onRemoveItem(row.index)}
                          />
                        </div>
                      );
                    },
                  },
                ]}
              />
            ) : null}
            <TableModal
              open={isAddingItem}
              onCancel={() => setIsAddingItem(false)}
              onOk={() => {
                onOkAddModal();
              }}
            >
              <Table<LocationTableItem>
                tableId={addTableId}
                title={"Select Locations to Add"}
                selectMode="multi"
                selectKey="selfLink"
                selections={selectionsToAdd}
                onSelectionsChange={(keys, items) => {
                  setSelectionsToAdd(keys);
                  setSelectionItems(items!);
                }}
                data={addLocationTableItems}
                headerRenderer={() => {
                  return (
                    <>
                      <TableHeaderRefreshButton onClick={qData.fetchItems} />
                      <CustomTableCPLNQuery
                        tableId={addTableId}
                        kind={addTableKind}
                        onQuery={qData.setQuery}
                        isLoading={qData.isLoadingQueried}
                      />
                    </>
                  );
                }}
                enableFilter
                isLoading={qData.isLoading}
                onSortChange={qData.setSort}
                onSearchChange={qData.setSearch}
                isLoadingMore={qData.isLoadingMore}
                hasMore={qData.hasMore}
                onLoadMore={qData.fetchNextPage}
                columns={[
                  NameDescriptionColumn(undefined, { shouldForceTargetBlank: () => true }),
                  ProviderColumn(),
                  TagsColumn(),
                ]}
              />
            </TableModal>
            <Button
              htmlType={`button`}
              className="mt-4"
              type={"dashed"}
              icon={<PlusCircleOutlined />}
              onClick={() => setIsAddingItem(true)}
            >
              Add Location
            </Button>
          </CardContent>
        </Card>
      </div>
    </>
  );
};
